import React, { useEffect, useRef, useState } from 'react';
import './ContactPage.css';
import {
  ArrowIcon,
  BiancodeLogo,
  CheckIcon,
  CopyIcon,
  InstagramIcon,
  MailIcon,
  PinIcon,
  SocialLink,
} from './icons';
import { getStoredUtmParams } from '../../utils/utmManager';
import { lerAtribuicao, utmsParaOErp, consentimentoAtual, plataformaDoUltimoClique } from '../../utils/atribuicaoCookies';
import { getStoredReferrer } from '../../utils/referralManager';
import { fbPixel, novoEventId } from '../../utils/facebookPixel';
import { submitPartialLead, submitCompleteLead } from '../../services/leadService';
import { submitLeadToCrm, buildCrmPayload } from '../../services/crmLeadService';
import { FATURAMENTOS_ANUAIS, FAIXAS_FUNCIONARIOS, exigeQualificacaoEmpresa, qualificacaoEmpresaValida, dadosDaEmpresa } from './companyQualification';
import { trackEvent, identifyUser } from '../../utils/posthogManager';
import {
  getOrCreateTrackingId,
  markFormStartTime,
  getTimeOnFormSeconds,
} from '../../utils/trackingManager';
import {
  CHALLENGE_MAP,
  FASES,
  ORCAMENTOS,
  ORCAMENTO_ERP,
  PUBLICOS,
  TIPOS_PRODUTO,
  computeTier,
  sanitizePhone,
} from './leadMapping';

const PARTNERS = [
  { name: 'AgenIA', logo: '/partners/agenia.png' },
  { name: 'AgroCoribe', logo: '/partners/agrocoribe.png' },
  { name: 'Auge', logo: '/partners/auge.png' },
  { name: 'Flow Pages', logo: '/partners/flow-pages.png' },
  { name: 'Fyze', logo: '/partners/fyze.png' },
  { name: 'Gerlot', logo: '/partners/gerlot.png' },
  { name: 'Habilita', logo: '/partners/habilita.png' },
  { name: 'Quasar', logo: '/partners/quasar.png' },
  { name: 'Radar Política', logo: '/partners/radar-politica.png' },
  { name: 'Relprev', logo: '/partners/relprev.png' },
  { name: 'Talk AI', logo: '/partners/talk-ai.png' },
  { name: 'Tradyon', logo: '/partners/tradyon.png' },
  { name: 'Turkgt', logo: '/partners/turkgt.png' },
  { name: 'Vantage', logo: '/partners/vantage.png' },
];

// Parâmetros que o Raio-X do Vale manda na URL. Sem eles, a página é a de sempre.
const lerRaioX = () => {
  const p = new URLSearchParams(window.location.search);
  const numero = (chave) => {
    const valor = Number(p.get(chave));
    return p.get(chave) !== null && Number.isFinite(valor) && valor >= 0 ? valor : null;
  };
  const dados = {
    n: (p.get('n') || '').toLowerCase(),
    base: numero('raiox_base'),
    ultimo: numero('raiox_ultimo'),
    lanc: p.get('raiox_lanc') || '',
    // A mensalidade que ele escolheu na calculadora. É o que explica a receita
    // no card do ERP; lead antigo chega sem ela e segue válido.
    mensalidade: numero('raiox_mens'),
    receita: numero('raiox_receita'),
    vale: numero('raiox_vale'),
    prioridade: p.get('prioridade') === '1',
  };
  dados.veioDoRaioX = dados.receita !== null && dados.receita > 0;
  return dados;
};

const raiox = typeof window === 'undefined' ? { veioDoRaioX: false, n: '' } : lerRaioX();

const moeda = (valor) =>
  Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  company: '',
  role: '',
  stage: '',
  productType: '',
  audience: '',
  budget: '',
  annualRevenue: '',
  employeeCount: '',
  scenario: '',
  // Campo-armadilha: fica escondido e ninguém digita nele. Robô que preenche
  // tudo que encontra se entrega, e o servidor descarta sem gravar.
  website: '',
};

const LP_VARIANT = 'pagina-contato';

// Quanto o visitante espera antes de ver a confirmação, mesmo que algum
// serviço ainda esteja gravando. O lead já foi enviado; o que resta é
// confirmação de gravação, e isso não é problema de quem preencheu.
const SEND_TIMEOUT_MS = 4000;

// A medição pode esperar mais do que a tela: ela não é vista por ninguém, e
// desistir cedo é perder a conversão de quem está numa rede ruim.
const MEDICAO_TIMEOUT_MS = 15000;

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ContactPage = () => {
  const qualificarEmpresa = exigeQualificacaoEmpresa(window.location.pathname);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // O isSubmitting só vale no próximo render. Dois cliques no mesmo quadro
  // passavam os dois pela porta e a Meta contava duas conversões.
  const travaEnvio = useRef(false);
  const trackingId = useRef(getOrCreateTrackingId());
  const partialSaved = useRef(false);

  useEffect(() => {
    markFormStartTime();
    trackEvent('contact_page_viewed', { lp_variant: LP_VARIANT });
    // Um evento só para a Meta, com o nicho junto. No dataLayer continua
    // existindo o form_view, que o GA4 já acumula.
    fbPixel.trackCustom('ContactPageViewed', { lp_variant: LP_VARIANT, n: raiox.n || 'direto' });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_view',
      nicho: raiox.n || 'direto',
      origem: raiox.veioDoRaioX ? 'raiox' : 'direto',
    });
  }, []);

  useEffect(() => {
    if (!showSuccess) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowSuccess(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showSuccess]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const copyEmail = async () => {
    await navigator.clipboard.writeText('comercial@biancode.com.br');
    setEmailCopied(true);
    window.setTimeout(() => setEmailCopied(false), 1800);
  };

  // Lead parcial: dispara uma vez, assim que nome + telefone estão preenchidos.
  // Substitui (de forma simplificada) a captura por etapa do formulário antigo.
  const savePartialOnce = async () => {
    if (partialSaved.current) return;
    const phone = sanitizePhone(form.phone);
    if (!form.name.trim() || phone.length < 12) return;
    partialSaved.current = true;

    const utmParams = getStoredUtmParams();
    const referrer = getStoredReferrer();
    const data = {
      fullName: form.name.trim(),
      phone,
      status: 'partial',
      lastStep: 2,
      trackingId: trackingId.current,
      landingPage: window.location.href,
    };
    if (form.email.trim()) data.email = form.email.trim();
    if (utmParams && Object.keys(utmParams).length > 0) data.utmParams = utmParams;
    if (referrer) data.referrer = referrer;

    try {
      identifyUser(phone, { full_name: data.fullName });
      await submitPartialLead(data);
      trackEvent('lead_partial_saved', { last_step: 2, lp_variant: LP_VARIANT });
    } catch (error) {
      console.error('Error saving partial lead:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (qualificarEmpresa && !qualificacaoEmpresaValida(form)) return;
    if (travaEnvio.current) return;
    travaEnvio.current = true;

    // Campo-armadilha preenchido: ninguém digita nele. Nada sai da página,
    // nem envio nem evento, e a tela não diz o que aconteceu. Dizer "você
    // falhou" só ensina o robô a tentar de outro jeito.
    if (form.website.trim()) {
      console.warn('Envio descartado pelo campo-armadilha.');
      travaEnvio.current = false;
      return;
    }

    setIsSubmitting(true);

    const challenge = CHALLENGE_MAP[form.productType] || 'outro';
    const phone = sanitizePhone(form.phone);
    // O ERP guarda orçamento como número e lê o texto que mandamos. O rótulo
    // escolhido viaja inteiro em `orcamento_faixa`, dentro do buildCrmPayload.
    const orcamentoErp = ORCAMENTO_ERP[form.budget] ?? '';
    const { score, tier } = computeTier({
      budget: form.budget,
      role: form.role,
      stage: form.stage,
    });
    const lpSource = new URLSearchParams(window.location.search).get('lp_source') || (raiox.veioDoRaioX ? 'raiox-infoprodutor' : 'direct');
    // O cookie de .biancode.com.br entra onde a URL desta visita está calada:
    // quem veio do Raio-X trocou de site e chegou aqui sem parâmetro nenhum.
    const utmParams = utmsParaOErp(getStoredUtmParams());
    const atribuicao = lerAtribuicao();
    const plataforma = plataformaDoUltimoClique(utmParams);
    const consent = consentimentoAtual();
    const referrer = getStoredReferrer();

    const leadData = {
      fullName: form.name.trim(),
      email: form.email.trim(),
      phone,
      challenge,
      company: form.company.trim(),
      decisionMakerRole: form.role.trim(),
      budgetRange: form.budget,
      productStage: form.stage,
      audience: form.audience,
      frontendScore: score,
      frontendTier: tier,
      status: 'complete',
      lastStep: 8,
      trackingId: trackingId.current,
      landingPage: window.location.href,
      lpSource,
      // valores crus, exatamente como o lead escolheu na tela
      rawProductType: form.productType,
      rawBudget: form.budget,
      rawStage: form.stage,
      rawAudience: form.audience,
      rawScenario: form.scenario.trim(),
      ...(qualificarEmpresa ? { qualificacao_empresa: dadosDaEmpresa(form) } : {}),
    };
    if (utmParams && Object.keys(utmParams).length > 0) leadData.utmParams = utmParams;
    if (referrer) leadData.referrer = referrer;
    // Contexto do Raio-X: vai junto no lead e aparece para o comercial.
    if (raiox.veioDoRaioX) {
      leadData.raiox = {
        n: raiox.n,
        base: raiox.base,
        ultimoLancamento: raiox.ultimo,
        lancamentosAno: raiox.lanc,
        mensalidade: raiox.mensalidade,
        receitaMes: raiox.receita,
        receitaVale: raiox.vale,
        prioridade: raiox.prioridade,
      };
    }

    try {
      identifyUser(phone, { full_name: leadData.fullName, email: leadData.email });

      // ERP (crm-biancode) em paralelo com o Firebase. Só o lead
      // completo vai para o ERP: cada card criado avisa o comercial no
      // WhatsApp, então parcial de quem abandonou viraria spam para o time.
      // O id viaja junto no lead: quando a API de Conversões entrar, o servidor
      // manda o mesmo id e a Meta sabe que é a mesma conversão, não duas.
      const metaEventId = novoEventId();

      // O envio para o ERP fica com referência própria: a conversão da Meta
      // depende só dele. Pendurada no Promise.all, ela não disparava quando o
      // Firestore entrava em retry, que é justamente quando a rede está ruim.
      const envioCrm = submitLeadToCrm(
        buildCrmPayload({
          form,
          phone,
          orcamentoErp,
          score,
          tier,
          trackingId: trackingId.current,
          lpSource,
          lpVariant: LP_VARIANT,
          utmParams,
          raiox: raiox.veioDoRaioX ? raiox : null,
          metaEventId,
          atribuicao,
          consent,
          plataforma,
          // Campo-armadilha: só robô preenche o que ninguém vê. Quem cair aqui
          // recebe 200 do servidor e não vira lead.
          isca: form.website,
        })
      );
      // O Firebase não segura a medição: ele reenvia sozinho quando a rede
      // está ruim e cada serviço loga o próprio resultado.
      submitCompleteLead(leadData).catch((error) =>
        console.error('Envio do lead ao Firebase falhou:', error)
      );

      // A tela e a medição esperam coisas diferentes, de propósito.
      //
      // A tela não pode ficar refém de gravação de terceiro: o visitante já
      // fez a parte dele e vê a confirmação em poucos segundos.
      //
      // A medição espera o ERP responder, com folga. Evento de lead que
      // dispara sem lead existir é pior do que evento que falta, porque
      // corrompe o custo por lead de toda a campanha. Prender isso ao mesmo
      // tempo da tela fazia conexão lenta perder a conversão.
      const paraATela = Promise.race([envioCrm, esperar(SEND_TIMEOUT_MS)]);

      Promise.race([
        envioCrm,
        esperar(MEDICAO_TIMEOUT_MS).then(() => ({ success: false, error: 'tempo esgotado' })),
      ])
        .catch((error) => ({ success: false, error: String(error?.message || error) }))
        .then((resultadoCrm) => {
          window.dataLayer = window.dataLayer || [];

          // lead_id vazio com success é o 200 que o servidor devolve para robô
          // que caiu na armadilha. Não é lead, então não é conversão.
          if (!resultadoCrm?.success || !resultadoCrm?.leadId) {
            window.dataLayer.push({
              event: 'lead_error',
              form_id: 'contato',
              motivo: String(resultadoCrm?.error || 'desconhecido').slice(0, 80),
            });
            return;
          }

          // Daqui para baixo o lead existe no ERP, e só se chega aqui uma vez
          // por envio: clique duplo para na trava lá em cima e robô para no
          // campo-armadilha antes de qualquer envio.
          trackEvent('lead_form_submitted', {
            challenge,
            orcamento_estimado: form.budget,
            fase_produto: form.stage,
            tipo_produto: form.productType,
            publico_alvo: form.audience,
            cargo: form.role.trim(),
            time_on_form_seconds: getTimeOnFormSeconds(),
            lp_source: lpSource,
            lp_variant: LP_VARIANT,
          });

          // lead_submit é o nome que a landing de fábrica de software já usa, e
          // de propósito: uma tag só no GTM serve as duas páginas. É esta tag
          // que dispara o Lead do Meta; a página não chama mais o pixel
          // direto, senão o mesmo lead contaria duas conversões. O event_id
          // viaja no payload do ERP, que repete o evento pelo servidor, e a
          // Meta deduplica pelo id.
          window.dataLayer.push({
            event: 'lead_submit',
            event_id: metaEventId,
            lead_id: resultadoCrm.leadId,
            repetido: Boolean(resultadoCrm.repetido),
            form_id: 'contato',
            pagina: LP_VARIANT,
            origem: raiox.veioDoRaioX ? 'raiox' : 'direto',
            nicho: raiox.n || 'direto',
            orcamento_estimado: form.budget,
            prioridade: raiox.prioridade ? 'sim' : 'nao',
          });

          window.dataLayer.push({
            event: 'form_submit',
            nicho: raiox.n || 'direto',
            orcamento_estimado: form.budget,
            prioridade: raiox.prioridade ? 'sim' : 'nao',
            origem: raiox.veioDoRaioX ? 'raiox' : 'direto',
          });

          window.dataLayer.push({
            event: 'lead_form_submitted',
            lp_variant: LP_VARIANT,
            lp_source: lpSource,
            lead_score: score,
            lead_tier: tier,
            lead_challenge: challenge,
            lead_budget: form.budget,
            lead_stage: form.stage,
            lead_audience: form.audience,
            tracking_id: trackingId.current,
          });
        });

      await paraATela;
    } catch (error) {
      console.error('Error submitting lead:', error);
    } finally {
      travaEnvio.current = false;
      setIsSubmitting(false);
      setShowSuccess(true);
    }
  };

  return (
    <main className="biancode-contact-page">
      <nav className="nav shell" aria-label="Navegação principal">
        <a className="brand" href="#inicio">
          <BiancodeLogo />
        </a>
        <div className="nav-links">
          <a className="active" href="#inicio">
            Home
          </a>
          <a href="https://biancode.com.br/quem-somos/">Quem Somos</a>
          <a href="https://biancode.com.br/nossas-solucoes/">Nossas soluções</a>
          <a href="https://biancode.com.br/cases/">Cases</a>
          <a href="https://biancode.com.br/oportunidades/">Oportunidades</a>
          <a href="https://biancode.com.br/blog">Blog</a>
        </div>
        <a className="nav-cta" href="#contato">
          <span className="nav-cta-label">AGENDAR CONSULTORIA</span> <ArrowIcon />
        </a>
        <button
          className={`menu-toggle${menuOpen ? ' open' : ''}`}
          type="button"
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className={`mobile-menu${menuOpen ? ' open' : ''}`} id="mobile-menu">
          <a href="#inicio" onClick={() => setMenuOpen(false)}>
            Home
          </a>
          <a href="https://biancode.com.br/quem-somos/">Quem Somos</a>
          <a href="https://biancode.com.br/nossas-solucoes/">Nossas soluções</a>
          <a href="https://biancode.com.br/cases/">Cases</a>
          <a href="https://biancode.com.br/oportunidades/">Oportunidades</a>
          <a href="https://biancode.com.br/blog">Blog</a>
          <div className="mobile-menu-socials">
            <SocialLink label="Instagram da BianCode" href="https://www.instagram.com/biancodetech">
              <InstagramIcon />
            </SocialLink>
            <SocialLink
              label="LinkedIn da BianCode"
              href="https://www.linkedin.com/company/biancode-software-house/"
            >
              in
            </SocialLink>
          </div>
        </div>
      </nav>

      <header className="hero" id="inicio">
        <div className="hero-art" aria-hidden="true">
          <div className="hero-video-wrap">
            <video
              className="hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster="/hero-orbit-poster.jpg"
            >
              <source src="/hero-orbit.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="code-lines">
            {'{'}
            <br />
            {'  strategy: scale;'}
            <br />
            {'  software: future;'}
            <br />
            {'  partner: true;'}
            <br />
            {'}'}
          </div>
        </div>
        <div className="hero-copy shell">
          <span className="desktop-only">ENGENHARIA DE SOFTWARE PARA EMPRESAS</span>
          <span className="mobile-only">SOFTWARE PARA EMPRESAS</span>
          <h1>
            <span className="desktop-only">
              Software sob medida <br />
              para sua empresa crescer.
            </span>
            <span className="mobile-only">Software para sua empresa crescer.</span>
          </h1>
          <p className="desktop-only">
            Transformamos desafios de negócio em sistemas, automações e produtos digitais que reduzem
            custos, aumentam a eficiência e aceleram resultados.
          </p>
          <p className="mobile-only">Conte seu desafio e receba um diagnóstico do nosso time.</p>
        </div>
      </header>

      <section className="contact-section" id="contato">
        <div className="section-heading">
          <span>CONTATO</span>
          <h2>Fale com a BianCode</h2>
        </div>
        <div className="contact-grid shell">
          <aside className="contact-copy">
            <h3>TECNOLOGIA PARA SUA EMPRESA CRESCER</h3>
            <p>
              Se a sua empresa precisa ganhar eficiência, integrar operações ou transformar uma ideia
              em software, a BianCode desenha o caminho certo para tirar o projeto do papel.
            </p>
            <p>
              Unimos engenharia consultiva, IA e visão de negócio para reduzir riscos, acelerar
              resultados e construir soluções preparadas para escalar.
            </p>
            <address>
              <div className="contact-line">
                <span className="icon-box">
                  <PinIcon />
                </span>
                <div>
                  <strong>Atendimento</strong>
                  <small>Online ou presencial em Vitória - ES e Jundiaí - SP</small>
                </div>
              </div>
              <div className="contact-line email-line">
                <span className="icon-box">
                  <MailIcon />
                </span>
                <div className="email-content">
                  <strong>E-mail</strong>
                  <div className="email-value">
                    <small>comercial@biancode.com.br</small>
                    <button
                      className={`copy-email${emailCopied ? ' copied' : ''}`}
                      type="button"
                      onClick={copyEmail}
                      aria-label={emailCopied ? 'E-mail copiado' : 'Copiar e-mail'}
                      title={emailCopied ? 'Copiado' : 'Copiar e-mail'}
                    >
                      {emailCopied ? <CheckIcon /> : <CopyIcon />}
                    </button>
                  </div>
                </div>
              </div>
            </address>
            <div className="social-block">
              <strong>Redes sociais</strong>
              <div className="socials">
                <SocialLink
                  label="Instagram da BianCode"
                  href="https://www.instagram.com/biancodetech"
                >
                  <InstagramIcon />
                </SocialLink>
                <SocialLink
                  label="LinkedIn da BianCode"
                  href="https://www.linkedin.com/company/biancode-software-house/"
                >
                  in
                </SocialLink>
              </div>
            </div>
          </aside>

          <form className={`contact-card${raiox.veioDoRaioX ? ' contact-card--raiox' : ''}`} onSubmit={handleSubmit}>
            {/* Campo-armadilha. aria-hidden e tabIndex -1 para leitor de tela e
                teclado nunca chegarem nele; quem preenche é robô. */}
            <input
              type="text"
              name="website"
              className="campo-isca"
              value={form.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <h3>
              {raiox.veioDoRaioX
                ? `Sua base rende ${moeda(raiox.receita)} por mês. Vamos conversar sobre isso.`
                : 'CONTE SOBRE O SEU PROJETO'}
            </h3>
            <div className="form-row">
              <label className="campo campo-nome">
                Nome e sobrenome *
                <input
                  name="name"
                  autoComplete="name"
                  placeholder="Nome Sobrenome"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </label>
              <label className="campo campo-email">
                E-mail corporativo *
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="nome@empresa.com.br"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>
            <div className="form-row">
              <label className="campo campo-telefone">
                Celular *
                <input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={savePartialOnce}
                  required
                />
              </label>
              <label className="campo campo-empresa">
                Empresa *
                <input
                  name="company"
                  autoComplete="organization"
                  placeholder="Nome da sua empresa"
                  value={form.company}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>
            <label className="campo campo-cargo">
              Seu cargo *
              <input
                name="role"
                autoComplete="organization-title"
                placeholder="Sua posição na empresa"
                value={form.role}
                onChange={handleChange}
                required
              />
            </label>
            {qualificarEmpresa && (
              <div className="form-row">
                <label className="campo campo-faturamento-anual">
                  Faturamento real da empresa nos últimos 12 meses *
                  <select name="annualRevenue" value={form.annualRevenue} onChange={handleChange} required>
                    <option value="" disabled>Selecione</option>
                    {FATURAMENTOS_ANUAIS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <small>Receita já faturada, não a projeção do simulador nem o orçamento do projeto.</small>
                </label>
                <label className="campo campo-funcionarios">
                  Quantas pessoas trabalham na empresa hoje? *
                  <select name="employeeCount" value={form.employeeCount} onChange={handleChange} required>
                    <option value="" disabled>Selecione</option>
                    {FAIXAS_FUNCIONARIOS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
              </div>
            )}
            <div className="form-row">
              <label className="campo campo-fase">
                Em que fase está o produto? *
                <select name="stage" value={form.stage} onChange={handleChange} required>
                  <option value="" disabled>
                    Selecione
                  </option>
                  {FASES.map((fase) => (
                    <option key={fase}>{fase}</option>
                  ))}
                </select>
              </label>
              <label className="campo campo-tipo">
                Tipo de produto *
                <select
                  name="productType"
                  value={form.productType}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Selecione
                  </option>
                  {TIPOS_PRODUTO.map((tipo) => (
                    <option key={tipo}>{tipo}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label className="campo campo-publico">
                Público-alvo *
                <select name="audience" value={form.audience} onChange={handleChange} required>
                  <option value="" disabled>
                    Selecione
                  </option>
                  {PUBLICOS.map((publico) => (
                    <option key={publico}>{publico}</option>
                  ))}
                </select>
              </label>
              <label className="campo campo-orcamento">
                Orçamento estimado *
                <select name="budget" value={form.budget} onChange={handleChange} required>
                  <option value="" disabled>
                    Selecione
                  </option>
                  {ORCAMENTOS.map((faixa) => (
                    <option key={faixa}>{faixa}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="campo campo-cenario">
              {/* Opcional de propósito: quem escreve, escreve; exigir texto
                  longo é onde formulário perde quem estava quase enviando. */}
              Conte um pouco sobre o cenário
              <textarea
                name="scenario"
                rows={4}
                placeholder="Um ou dois parágrafos com o contexto, o problema e o que espera da conversa."
                value={form.scenario}
                onChange={handleChange}
              />
            </label>
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                'ENVIANDO...'
              ) : showSuccess ? (
                'MENSAGEM ENVIADA ✓'
              ) : (
                <>
                  AGENDAR DIAGNÓSTICO <ArrowIcon />
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      <section className="partners" id="parceiros">
        <div className="section-heading">
          <span>QUEM JÁ CONFIOU</span>
          <h2>Parceiros da BianCode</h2>
        </div>
        <div className="partner-grid shell">
          {PARTNERS.map((partner, index) => (
            <div className={`partner partner-${index}`} key={partner.name}>
              <img src={partner.logo} alt={`Logo ${partner.name}`} loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      <footer id="rodape">
        <div className="footer-main shell">
          <a className="brand footer-brand" href="#inicio">
            <BiancodeLogo />
          </a>
          <p>Tecnologia para empresas que precisam escalar com segurança.</p>
          <div className="socials footer-socials">
            <SocialLink label="Instagram da BianCode" href="https://www.instagram.com/biancodetech">
              <InstagramIcon />
            </SocialLink>
            <SocialLink
              label="LinkedIn da BianCode"
              href="https://www.linkedin.com/company/biancode-software-house/"
            >
              in
            </SocialLink>
          </div>
        </div>
        <div className="footer-bar">
          <span>●</span>
          <small>Biancode®, 2026. Todos os Direitos Reservados.</small>
          <div>
            <a href="https://biancode.com.br/politica-do-codigo-de-conduta/">Código de Conduta</a>
            <a href="https://biancode.com.br/politica-de-privacidade/">Privacidade</a>
          </div>
        </div>
      </footer>

      {showSuccess && (
        <div className="success-modal-backdrop" onMouseDown={() => setShowSuccess(false)}>
          <section
            className="success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="success-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              className="success-modal-close"
              type="button"
              onClick={() => setShowSuccess(false)}
              aria-label="Fechar mensagem"
            >
              ×
            </button>
            <div className="success-modal-visual" aria-hidden="true">
              <div className="success-modal-icon">
                <CheckIcon />
              </div>
            </div>
            <div className="success-modal-content">
              <span>SOLICITAÇÃO ENVIADA</span>
              <h2 id="success-modal-title">Obrigado pelo seu interesse.</h2>
              <p>
                Nossa equipe irá analisar o cenário, o momento e os objetivos da sua empresa.
              </p>
              <div className="success-modal-highlight">
                <strong>Análise de aderência comercial</strong>
                <small>
                  Se o projeto estiver alinhado ao perfil atendido pela BianCode, um especialista
                  entrará em contato para avançarmos com um diagnóstico estratégico.
                </small>
              </div>
              <div className="success-modal-actions">
                <button
                  className="success-modal-secondary"
                  type="button"
                  onClick={() => setShowSuccess(false)}
                >
                  CONTINUAR NO SITE
                </button>
                <a
                  className="success-modal-action"
                  href="https://biancode.com.br"
                  target="_blank"
                  rel="noreferrer"
                >
                  CONHECER A BIANCODE <ArrowIcon />
                </a>
              </div>
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default ContactPage;
